import { api_configuration } from "../../common";
import gui_definition_types = api_configuration.libraries.gui_definition_lib.types;
import api_control_library = api_configuration.libraries.gui_definition_lib.libraries.control_bundle_lib.libraries.controls_lib.types.control_library.alan_api;
import api_model = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_client_bindings = api_configuration.libraries.gui_definition_lib.libraries.client_lib.types.client_bindings.alan_api;

export type DecoratedSchemas = {
	main: gui_definition_types.main.alan_api.Cmain;
	settings: gui_definition_types.settings.alan_api.Csettings;
	widgets: Record<string, gui_definition_types.widget.alan_api.Cwidget>;
	gui_model: gui_definition_types.gui_model.alan_api.Cgui_model;
	views: Record<string, gui_definition_types.views.alan_api.Cviews>;
	phrases: gui_definition_types.phrases.alan_api.Cphrases;
	client_bindings: api_client_bindings.Cclient_bindings;
	controls: Record<string, api_control_library.Ccontrol_library>;
	translations: Record<string, gui_definition_types.translations.alan_api.Ctranslations>;
	reports: Record<string, gui_definition_types.parameters.alan_api.Cparameters>;
};

export function decorate(
	raw_schemas: {
		main: any;
		settings: any;
		widgets: { [key: string]: any };
		gui_model: any;
		views: { [name: string]: any };
		phrases: any;
		client_bindings: any;
		controls: { [name: string]: any };
		translations: { [name: string]: any };
		reports: { [name: string]: any };
	},
	application_model: api_model.Capplication
): DecoratedSchemas {
	const client_bindings =
		api_configuration.libraries.gui_definition_lib.libraries.client_lib.types.client_bindings.alan_api.Cclient_bindings.create(
			raw_schemas.client_bindings
		);

	const controls: Record<string, api_control_library.Ccontrol_library> = {};
	Object.keys(raw_schemas.controls).forEach((control_name) => {
		Object.defineProperty(controls, control_name, {
			get: function () {
				delete controls[control_name];
				const control = api_control_library.Ccontrol_library.create(raw_schemas.controls[control_name], {
					controls: controls,
				});
				controls[control_name] = control;
				control.key = control_name;
				return control;
			},
			enumerable: true,
			configurable: true,
		});
	});
	const phrases = gui_definition_types.phrases.alan_api.Cphrases.create(raw_schemas.phrases);
	const widgets = Object.keys(raw_schemas.widgets).reduce<{
		[key: string]: api_configuration.libraries.gui_definition_lib.types.widget.alan_api.Cwidget;
	}>(function (obj, widget_name) {
		obj[widget_name] = gui_definition_types.widget.alan_api.Cwidget.create(raw_schemas.widgets[widget_name], {
			client_bindings: client_bindings,
			control_library: controls,
			phrases: phrases,
		});
		obj[widget_name].key = widget_name;
		return obj;
	}, {});
	const translations = Object.keys(raw_schemas.translations).reduce<{
		[key: string]: gui_definition_types.translations.alan_api.Ctranslations;
	}>(function (obj, language_name) {
		obj[language_name] = gui_definition_types.translations.alan_api.Ctranslations.create(
			raw_schemas.translations[language_name],
			{
				phrases: phrases,
			}
		);
		return obj;
	}, {});
	const reports = Object.keys(raw_schemas.reports).reduce<{
		[name: string]: gui_definition_types.parameters.alan_api.Cparameters;
	}>((obj, report_name) => {
		obj[report_name] = gui_definition_types.parameters.alan_api.Cparameters.create(raw_schemas.reports[report_name], {
			application: application_model,
		});
		return obj;
	}, {});
	const settings = gui_definition_types.settings.alan_api.Csettings.create(raw_schemas.settings, {
		application: application_model,
		languages: translations,
		phrases: phrases,
	});
	const views: { [name: string]: gui_definition_types.views.alan_api.Cviews } = {};
	const views_proxy = new Proxy(
		{},
		{
			get: (_target, view_name: string) => {
				let view = views[view_name];
				if (view !== undefined) return view;
				view = views[view_name] = gui_definition_types.views.alan_api.Cviews.create(raw_schemas.views[view_name], {
					application: application_model,
					client_bindings: client_bindings,
					widgets: widgets,
					gui_model: gui_model,
					phrases: phrases,
					settings: settings,
					views: views_proxy,
					reports: reports,
				});
				view.key = view_name;
				return view;
			},
		}
	);
	const gui_model = gui_definition_types.gui_model.alan_api.Cgui_model.create(raw_schemas.gui_model, {
		application: application_model,
	});
	const main = api_configuration.libraries.gui_definition_lib.types.main.alan_api.Cmain.create(raw_schemas.main, {
		settings: settings,
		views: views_proxy,
		gui_model: gui_model,
	});

	return {
		main: main,
		settings: settings,
		widgets: widgets,
		gui_model: gui_model,
		views: views,
		phrases: phrases,
		client_bindings: client_bindings,
		controls: controls,
		translations: translations,
		reports: reports,
	};
}
